import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, mondayOf, addDays, todayISO, toLocalISODate, formatDate, parseLocalDate, DAY_NAMES, DAY_SHORT } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, CaretLeft, CaretRight, Lightning, CopySimple, Moon, Trophy, ClockCounterClockwise, CheckCircle, ArrowCounterClockwise, Coins, DotsThreeVertical, CalendarBlank } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import ShareProgramButton from "@/components/app/ShareProgramButton";
import ClearWeekButton from "@/components/app/ClearWeekButton";
import CoachJournal from "@/components/blocks/CoachJournal";
import { toast } from "sonner";
import { BLOCK_TYPES } from "@/components/blocks/blockMeta";
import BlockForm from "@/components/blocks/BlockForm";
import BlockTypePicker from "@/components/blocks/BlockTypePicker";
import BlockCard from "@/components/blocks/BlockCard";
import QuickLogRow from "@/components/blocks/QuickLogRow";
import BackButton from "@/components/app/BackButton";
import DayActionsDialog from "@/components/blocks/DayActionsDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = { performed: "Present", forfeited: "Forfeited", sick: "Sick", excused: "Excused", courtesy: "Courtesy" };
const STATUS_STYLE = {
  performed: "bg-moss text-white hover:bg-moss-hover",
  forfeited: "bg-terracotta text-white hover:bg-terracotta/90",
  sick: "bg-warning text-white hover:bg-warning/90",
  excused: "bg-warning/80 text-white hover:bg-warning/70",
  courtesy: "bg-sage text-moss hover:bg-sage/80",
};

export default function WeekView({ athleteIdOverride }) {
  const params = useParams();
  const athleteId = athleteIdOverride || params.athleteId;
  const { user } = useAuth();
  // Athletes can edit their OWN week (self-programming when they train solo);
  // coaches/admins can edit any athlete they have access to.
  const canEditCore = user.role === "admin" || user.role === "coach" || athleteId === user.id;
  // Class-pack attendance check-ins are a coach/admin function — athletes
  // never check themselves in.
  const isCoachStaff = user.role === "admin" || user.role === "coach";

  const [searchParams] = useSearchParams();
  const gotoDate = searchParams.get("date");
  const [weekStart, setWeekStart] = useState(() => mondayOf(gotoDate || undefined));
  const nav = useNavigate();
  const [program, setProgram] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blockPickerDay, setBlockPickerDay] = useState(null);
  const [pendingBlockType, setPendingBlockType] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [activePack, setActivePack] = useState(null);
  // Map of normalised block-title → prior attempts (for conditioning blocks).
  // Lets BlockCard surface "you did this on May 12: 4:32" inline.
  const [blockHistoryByTitle, setBlockHistoryByTitle] = useState({});

  const loadProgram = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/by-week/${athleteId}/${weekStart}`);
      setProgram(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [athleteId, weekStart]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/users/${athleteId}`);
        setAthlete(data);
      } catch {
        /* ignore */
      }
    })();
  }, [athleteId]);

  // Coach view: load attendance + active class pack so we can render check-ins
  const loadCoachAttendanceState = useCallback(async () => {
    if (user.role === "athlete") return;
    try {
      const weekEnd = addDays(weekStart, 6);
      const [{ data: att }, { data: packs }] = await Promise.all([
        api.get("/attendance", { params: { athlete_id: athleteId, from_: weekStart, to: weekEnd } }),
        api.get(`/athletes/${athleteId}/packages`),
      ]);
      setAttendance(att);
      // Pick the first active (sessions_remaining > 0, not expired) pack for display
      const now = new Date();
      const active = (packs || []).find((p) =>
        p.sessions_remaining > 0 && (!p.expires_at || new Date(p.expires_at) > now)
      );
      setActivePack(active || null);
    } catch {
      /* coach features are optional */
    }
  }, [athleteId, weekStart, user.role]);

  useEffect(() => {
    loadCoachAttendanceState();
  }, [loadCoachAttendanceState]);

  const findAttendance = (dateStr) => attendance.find((a) => a.date === dateStr && !a.restored_at);

  // Apply a session disposition for a given day. If no check-in exists yet
  // we create one with the chosen status; if one exists we either flip its
  // status (different choice) or undo it (same choice clicked again).
  const setAttendanceStatus = async (dateStr, dayIdx, status) => {
    const existing = findAttendance(dateStr);
    try {
      if (existing) {
        const currentStatus = existing.status || "performed";
        if (currentStatus === status) {
          await api.post(`/attendance/check-in/${existing.id}/restore`);
          toast.success("Undone — credit refunded");
        } else {
          await api.patch(`/attendance/${existing.id}/status`, { status });
          toast.success(STATUS_LABEL[status]);
        }
      } else {
        const { data } = await api.post("/attendance/check-in", {
          athlete_id: athleteId,
          date: dateStr,
          program_id: program?.id || null,
          day_of_week: dayIdx,
        });
        if (status !== "performed") {
          await api.patch(`/attendance/${data.id}/status`, { status });
        }
        toast.success(status === "performed" ? "Checked in" : STATUS_LABEL[status]);
      }
      loadCoachAttendanceState();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { loadProgram(); }, [loadProgram]);

  // Load prior block attempts for this athlete so each conditioning card can
  // show "Previously: 4:32 (May 12)…". One fetch per athlete; results aren't
  // affected by week navigation so we don't re-fetch on weekStart change.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/stats/block-prs/${athleteId}`);
        if (!alive) return;
        const map = {};
        for (const rec of data || []) {
          const key = (rec.title || "").trim().toLowerCase();
          if (key) map[key] = rec;
        }
        setBlockHistoryByTitle(map);
      } catch {
        // Non-fatal — the conditioning cards just won't show history.
      }
    })();
    return () => { alive = false; };
  }, [athleteId]);

  const shiftWeek = (n) => setWeekStart(addDays(weekStart, n * 7));

  // ─── block / movement mutators ─────────────────────────────
  const addBlock = async (dayIdx, type, blockData = null, draftMovements = []) => {
    try {
      const body = blockData || { type, scheme_config: {} };
      const { data } = await api.post(`/programs/${program.id}/days/${dayIdx}/blocks`, body);
      const blockWithMovements = { ...data, movements: data.movements || [] };
      for (const mv of draftMovements) {
        if (!mv.name) continue;
        try {
          const { data: created } = await api.post(
            `/programs/${program.id}/days/${dayIdx}/blocks/${data.id}/movements`,
            mv,
          );
          blockWithMovements.movements.push(created);
        } catch (err) {
          toast.error(`${mv.name}: ${formatApiError(err)}`);
        }
      }
      setProgram((p) => {
        const days = p.days.map((d) => ({ ...d, blocks: [...(d.blocks || [])] }));
        days[dayIdx].blocks.push(blockWithMovements);
        return { ...p, days };
      });
      toast.success(`Section added${draftMovements.length ? ` with ${draftMovements.length} movement${draftMovements.length > 1 ? "s" : ""}` : ""}`);
      return blockWithMovements;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    }
  };

  const onBlockEdited = (dayIdx, updated) => setProgram((p) => {
    const days = p.days.map((d) => ({ ...d, blocks: [...(d.blocks || [])] }));
    days[dayIdx].blocks = days[dayIdx].blocks.map((b) => (b.id === updated.id ? { ...b, ...updated } : b));
    return { ...p, days };
  });
  const onBlockDeleted = (dayIdx, blockId) => setProgram((p) => {
    const days = p.days.map((d) => ({ ...d, blocks: (d.blocks || []).filter((b) => b.id !== blockId) }));
    return { ...p, days };
  });

  // Move a block up (dir=-1) or down (dir=+1) within its day. Persists the
  // new order to /reorder-blocks. Guarded against out-of-range moves.
  const onReorderBlock = async (dayIdx, blockId, dir) => {
    if (!program) return;
    const blocks = [...(program.days[dayIdx].blocks || [])];
    const idx = blocks.findIndex((b) => b.id === blockId);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= blocks.length) return;
    [blocks[idx], blocks[target]] = [blocks[target], blocks[idx]];
    const ids = blocks.map((b) => b.id);
    // Optimistic UI update.
    setProgram((p) => {
      const days = p.days.map((d, i) => (i === dayIdx ? { ...d, blocks } : d));
      return { ...p, days };
    });
    try {
      await api.patch(`/programs/${program.id}/days/${dayIdx}/reorder-blocks`, { block_ids: ids });
    } catch (e) {
      toast.error(formatApiError(e));
      loadProgram(); // reload to revert
    }
  };
  const onMovementAdded = (dayIdx, blockId, mv) => setProgram((p) => {
    const days = p.days.map((d) => ({ ...d, blocks: (d.blocks || []).map((b) => ({ ...b, movements: [...(b.movements || [])] })) }));
    const blk = days[dayIdx].blocks.find((b) => b.id === blockId);
    if (blk) blk.movements.push(mv);
    return { ...p, days };
  });
  const onMovementEdited = (dayIdx, blockId, updated) => setProgram((p) => {
    const days = p.days.map((d) => ({ ...d, blocks: (d.blocks || []).map((b) => ({ ...b, movements: (b.movements || []).map((m) => (m.id === updated.id ? updated : m)) })) }));
    return { ...p, days };
  });
  const onMovementDeleted = (dayIdx, blockId, mvId) => setProgram((p) => {
    const days = p.days.map((d) => ({ ...d, blocks: (d.blocks || []).map((b) => ({ ...b, movements: (b.movements || []).filter((m) => m.id !== mvId) })) }));
    return { ...p, days };
  });
  const onMovementsReordered = (dayIdx, blockId, newOrderedMovements) => setProgram((p) => {
    const days = p.days.map((d, i) => i !== dayIdx ? d : ({
      ...d,
      blocks: (d.blocks || []).map((b) => b.id === blockId ? { ...b, movements: newOrderedMovements } : b),
    }));
    return { ...p, days };
  });

  const pickBlockType = async (type) => {
    const dayIdx = blockPickerDay;
    setBlockPickerDay(null);
    if (type === "strength") {
      await addBlock(dayIdx, "strength");
    } else {
      setPendingBlockType({ dayIdx, type });
    }
  };

  const insertWodAsBlock = async (wod) => {
    const dayIdx = blockPickerDay;
    setBlockPickerDay(null);
    try {
      const { data } = await api.post(`/programs/${program.id}/days/${dayIdx}/blocks/from-wod/${wod.slug}`);
      setProgram((p) => {
        const days = p.days.map((d) => ({ ...d, blocks: [...(d.blocks || [])] }));
        days[dayIdx].blocks.push(data);
        return { ...p, days };
      });
      toast.success(`${wod.name} added to ${DAY_NAMES[dayIdx]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const insertTemplateAsBlock = async (tpl) => {
    const dayIdx = blockPickerDay;
    setBlockPickerDay(null);
    try {
      const { data } = await api.post(`/programs/${program.id}/days/${dayIdx}/blocks/from-template/${tpl.id}`);
      setProgram((p) => {
        const days = p.days.map((d) => ({ ...d, blocks: [...(d.blocks || [])] }));
        days[dayIdx].blocks.push(data);
        return { ...p, days };
      });
      toast.success(`${tpl.title} added to ${DAY_NAMES[dayIdx]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ─── day rendering ──────────────────────────────────────────
  const dayDates = useMemo(() => DAY_NAMES.map((_, i) => addDays(weekStart, i)), [weekStart]);
  const today = todayISO();
  const todayIdxInWeek = dayDates.indexOf(today);
  const gotoIdxInWeek = gotoDate ? dayDates.indexOf(gotoDate) : -1;
  const initialActiveDay = gotoIdxInWeek >= 0
    ? gotoIdxInWeek
    : (todayIdxInWeek >= 0 ? todayIdxInWeek : 0);
  const [activeDay, setActiveDay] = useState(initialActiveDay);
  const touchStartX = useRef(null);

  // Coach / athlete asked for a confirmation guard so an accidental tap on
  // a day tab or an inadvertent swipe on mobile can't yank the view away
  // from the day currently being edited. Every day-switch attempt goes
  // through `safeSetActiveDay`, which stages the target in `pendingDay`
  // and pops an AlertDialog; the switch only commits after "Switch day".
  const [pendingDay, setPendingDay] = useState(null);
  const safeSetActiveDay = (next) => {
    if (next === activeDay || next < 0 || next > 6) return;
    setPendingDay(next);
  };
  const confirmDaySwitch = () => {
    if (pendingDay !== null) setActiveDay(pendingDay);
    setPendingDay(null);
  };
  useEffect(() => {
    if (gotoIdxInWeek >= 0) {
      setActiveDay(gotoIdxInWeek);
    } else {
      setActiveDay(todayIdxInWeek >= 0 ? todayIdxInWeek : 0);
    }
  }, [weekStart, todayIdxInWeek, gotoIdxInWeek]);

  // Honour `?date=YYYY-MM-DD` — jump to the week containing that date.
  useEffect(() => {
    if (!gotoDate) return;
    const targetMonday = mondayOf(gotoDate);
    setWeekStart((cur) => (cur === targetMonday ? cur : targetMonday));
  }, [gotoDate]);

  const toggleRestDay = async (dayIdx) => {
    const cur = program.days[dayIdx].is_rest_day;
    try {
      const { data } = await api.patch(`/programs/${program.id}/days/${dayIdx}`, { is_rest_day: !cur });
      setProgram((p) => {
        const days = p.days.map((d, i) => i === dayIdx ? { ...d, ...data } : d);
        return { ...p, days };
      });
      toast.success(data.is_rest_day ? `${DAY_NAMES[dayIdx]} marked as rest day` : `${DAY_NAMES[dayIdx]} is back to active`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const renderDayCard = (d, i, layout) => {
    const isToday = dayDates[i] === today;
    const blocks = d.blocks || [];
    const isRest = !!d.is_rest_day;
    const att = findAttendance(dayDates[i]);
    const showCheckIn = isCoachStaff && !isRest;
    return (
      <div key={i} className={`hs-card p-4 sm:p-5 ${isRest ? "bg-sage/20" : ""}`} data-testid={`day-card-${i}-${layout}`}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className={`hs-label ${isToday ? "text-terracotta" : ""}`}>
              {DAY_SHORT[i]} · {formatDate(dayDates[i], { month: "short", day: "numeric" })}
              {isToday && " · today"}
              {isRest && " · rest"}
              {att && ` · ${(STATUS_LABEL[att.status || "performed"] || "Checked in").toLowerCase()}`}
            </div>
            <h3 className="font-heading text-xl font-semibold mt-1">{DAY_NAMES[i]}</h3>
          </div>
          {canEditCore && (
            <div className="flex items-center gap-1.5 shrink-0">
              {showCheckIn && (() => {
                const currentStatus = att ? (att.status || "performed") : null;
                const idleStyle = "bg-terracotta/10 text-terracotta hover:bg-terracotta hover:text-white";
                const label = att ? STATUS_LABEL[currentStatus] : "Check in";
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      className={`h-9 px-3 rounded-xl text-xs font-semibold uppercase tracking-[0.12em] inline-flex items-center gap-1.5 transition-colors ${
                        att ? STATUS_STYLE[currentStatus] : idleStyle
                      }`}
                      data-testid={`check-in-day-${i}-${layout}`}
                      title="Set attendance status"
                    >
                      <CheckCircle size={14} weight={att ? "fill" : "regular"} />
                      {label}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[160px]">
                      <DropdownMenuItem
                        onClick={() => setAttendanceStatus(dayDates[i], i, "performed")}
                        data-testid={`check-in-day-${i}-${layout}-performed`}
                      >
                        Check in
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setAttendanceStatus(dayDates[i], i, "sick")}
                        data-testid={`check-in-day-${i}-${layout}-sick`}
                      >
                        Sick
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setAttendanceStatus(dayDates[i], i, "excused")}
                        data-testid={`check-in-day-${i}-${layout}-excused`}
                      >
                        Excused
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setAttendanceStatus(dayDates[i], i, "forfeited")}
                        data-testid={`check-in-day-${i}-${layout}-forfeited`}
                      >
                        Forfeited
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setAttendanceStatus(dayDates[i], i, "courtesy")}
                        data-testid={`check-in-day-${i}-${layout}-courtesy`}
                      >
                        Courtesy
                      </DropdownMenuItem>
                      {att && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={async () => {
                              try {
                                await api.post(`/attendance/check-in/${att.id}/restore`);
                                toast.success("Undone — credit refunded");
                                loadCoachAttendanceState();
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="text-ink-3"
                            data-testid={`check-in-day-${i}-${layout}-undo`}
                          >
                            Undo / clear
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
              <button
                onClick={() => toggleRestDay(i)}
                className={`h-9 px-3 rounded-xl text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
                  isRest ? "bg-moss text-white" : "bg-muted2 text-ink-2 hover:bg-sage"
                }`}
                data-testid={`toggle-rest-day-${i}-${layout}`}
                aria-label={`Toggle rest day for ${DAY_NAMES[i]}`}
                title={isRest ? "Marked as rest — click to re-activate" : "Mark this day as rest"}
              >
                {isRest ? "Rest on" : "Rest"}
              </button>
              {!isRest && (
                <Button
                  size="icon"
                  variant="secondary"
                  className="rounded-xl h-9 w-9"
                  onClick={() => setBlockPickerDay(i)}
                  data-testid={`add-movement-day-${i}-${layout}`}
                  aria-label={`Add section to ${DAY_NAMES[i]}`}
                >
                  <Plus size={16} />
                </Button>
              )}
              <DayActionsDialog
                programId={program?.id}
                dayIdx={i}
                blockCount={blocks.length}
                athleteId={athleteId}
                onDone={loadProgram}
                trigger={
                  <Button
                    size="icon"
                    variant="ghost"
                    className="rounded-xl h-9 w-9 hover:bg-muted2"
                    data-testid={`day-actions-trigger-${i}-${layout}`}
                    aria-label={`Day actions for ${DAY_NAMES[i]}`}
                    title="Move, copy, save or stamp the whole day"
                  >
                    <DotsThreeVertical size={18} weight="bold" />
                  </Button>
                }
              />
            </div>
          )}
        </div>

        <div className="mt-4 space-y-4">
          {isRest ? (
            <div className="py-10 text-center border border-dashed border-moss/40 rounded-xl" data-testid={`rest-day-banner-${i}-${layout}`}>
              <Moon size={28} weight="fill" className="mx-auto text-moss" />
              <p className="mt-2 font-heading text-lg font-semibold text-moss">Rest &amp; recover</p>
              <p className="mt-1 text-xs text-ink-3">Walk, breathe, sleep well. You earned it.</p>
            </div>
          ) : blocks.length === 0 ? (
            <div className="py-8 text-center text-sm text-ink-3 border border-dashed border-line rounded-xl">
              {canEditCore ? "No sections yet. Tap + to add a strength block, MetCon, AMRAP, EMOM, Tabata, or ladder." : "Nothing scheduled yet."}
            </div>
          ) : (
            blocks.map((blk, blkIdx) => (
              <BlockCard
                key={blk.id}
                blk={blk}
                program={program}
                dayIdx={i}
                canEditCore={canEditCore}
                athleteId={athleteId}
                userRole={user.role}
                priorAttempts={blockHistoryByTitle[(blk.title || "").trim().toLowerCase()] || null}
                blockIndex={blkIdx}
                totalBlocks={blocks.length}
                onReorderBlock={(dir) => onReorderBlock(i, blk.id, dir)}
                onBlockEdited={(b) => onBlockEdited(i, b)}
                onBlockDeleted={(id) => onBlockDeleted(i, id)}
                onMoved={loadProgram}
                onMovementAdded={(bid, mv) => onMovementAdded(i, bid, mv)}
                onMovementEdited={(bid, m) => onMovementEdited(i, bid, m)}
                onMovementDeleted={(bid, mid) => onMovementDeleted(i, bid, mid)}
                onMovementsReordered={(bid, list) => onMovementsReordered(i, bid, list)}
              />
            ))
          )}
          {user.role !== "athlete" && (
            <CoachJournal
              programId={program.id}
              dayIdx={i}
              initial={d.coach_note || ""}
              onSaved={(txt) => {
                setProgram((p) => {
                  const days = p.days.map((day, j) => (j === i ? { ...day, coach_note: txt || null } : day));
                  return { ...p, days };
                });
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto pb-24">
      {user.role !== "athlete" && (
        <div className="mb-2">
          <BackButton to="/people" label="All athletes" />
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="hs-label">Weekly program</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2">
            {athlete ? athlete.name : "Athlete"}
          </h1>
          <p className="text-ink-2 text-sm mt-1">
            Week of {formatDate(weekStart, { month: "long", day: "numeric", year: "numeric" })}
          </p>
          {user.role !== "athlete" && athlete && (
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <Link to={`/athletes/${athleteId}/records`}>
                <Button variant="secondary" className="rounded-xl h-9" data-testid="go-records">
                  <Trophy size={14} className="mr-1.5" weight="fill" /> View records
                </Button>
              </Link>
              <Link to={`/athletes/${athleteId}/history`}>
                <Button variant="secondary" className="rounded-xl h-9" data-testid="go-history">
                  <ClockCounterClockwise size={14} className="mr-1.5" /> View history
                </Button>
              </Link>
              {activePack ? (
                <Link to={`/athletes/${athleteId}/billing`}>
                  <div className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl font-data text-xs font-semibold border transition-colors ${
                    activePack.payment_status === "credit"
                      ? "bg-warning/10 text-warning border-warning/30 hover:bg-warning/20"
                      : "bg-sage text-moss border-sage hover:bg-sage/80"
                  }`} data-testid="coach-active-pack">
                    {activePack.payment_status === "credit" ? <Coins size={14} weight="fill" /> : <CheckCircle size={14} weight="fill" />}
                    {activePack.sessions_remaining}/{activePack.sessions_total} {activePack.package_name}
                    {activePack.payment_status === "credit" && " · credit"}
                  </div>
                </Link>
              ) : (
                <Link to={`/athletes/${athleteId}/billing`}>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-warning/10 text-warning font-data text-xs font-semibold border border-warning/30 hover:bg-warning/20"
                    data-testid="coach-no-pack"
                    title="Athlete has no active credits — click to grant"
                  >
                    No active pack
                  </button>
                </Link>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEditCore && program && (
            <Button
              variant="secondary"
              onClick={async () => {
                const nextWeek = addDays(weekStart, 7);
                if (!window.confirm(`Copy this week's program to the week of ${nextWeek}?`)) return;
                try {
                  await api.post(`/programs/${program.id}/copy-to`, { target_week_start: nextWeek });
                  toast.success(`Copied to week of ${nextWeek}`);
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
              className="rounded-xl"
              data-testid="copy-to-next-week-button"
            >
              <CopySimple size={14} className="mr-2" /> Copy to next week
            </Button>
          )}
          {isCoachStaff && program && (
            <ClearWeekButton program={program} weekStart={weekStart} onDone={loadProgram} />
          )}
          <Button variant="secondary" onClick={() => shiftWeek(-1)} className="rounded-xl" data-testid="prev-week-button"><CaretLeft size={16} /></Button>
          <Button variant="secondary" onClick={() => setWeekStart(mondayOf())} className="rounded-xl" data-testid="this-week-button">This week</Button>
          <Button variant="secondary" onClick={() => shiftWeek(1)} className="rounded-xl" data-testid="next-week-button"><CaretRight size={16} /></Button>
          <GoToDatePopover
            currentDate={dayDates[activeDay]}
            onPick={(iso) => {
              const target = parseLocalDate(iso);
              const targetMonday = mondayOf(iso);
              setWeekStart(targetMonday);
              // activeDay updates via effect after weekStart change, but set it
              // eagerly for snappier UX (day-of-week within new week).
              const dow = target.getDay();
              setActiveDay(dow === 0 ? 6 : dow - 1);
            }}
          />
          <ShareProgramButton
            program={program}
            dayIdx={activeDay}
            athleteName={athlete?.name}
            athleteId={athleteId}
            dayDates={dayDates}
          />
        </div>
      </div>

      {loading && <div className="mt-8 text-ink-3">Loading…</div>}

      {!loading && !program && (
        <div className="mt-8 hs-card p-10 text-center" data-testid="no-program-empty-state">
          <Moon size={28} weight="fill" className="mx-auto text-moss" />
          <p className="mt-3 font-heading text-xl font-semibold">No program for this week yet.</p>
          <p className="mt-1 text-sm text-ink-2 max-w-md mx-auto">
            {canEditCore
              ? "Use the navigation buttons above to find a week, then add a section to start programming."
              : "Your coach hasn't built this week yet. Tap “This week” to jump back, or check in soon."}
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <Button variant="secondary" onClick={() => setWeekStart(mondayOf())} className="rounded-xl" data-testid="empty-jump-this-week">This week</Button>
          </div>
        </div>
      )}

      {!loading && program && (
        <>
          {/* Mobile: day picker tabs + active day only */}
          <div className="md:hidden mt-6">
            <div className="-mx-4 px-4 overflow-x-auto no-scrollbar">
              <div className="flex gap-2 min-w-max pb-2">
                {DAY_NAMES.map((name, i) => {
                  const isToday = dayDates[i] === today;
                  const isActive = activeDay === i;
                  const dayBlocks = program.days[i].blocks || [];
                  const count = dayBlocks.reduce((acc, b) => acc + ((b.movements || []).length), 0);
                  return (
                    <button
                      key={i}
                      onClick={() => safeSetActiveDay(i)}
                      className={`flex flex-col items-center px-4 py-2.5 rounded-xl border transition-colors min-w-[68px] ${
                        isActive
                          ? "bg-moss text-white border-moss"
                          : "bg-surface text-ink border-line hover:bg-muted2"
                      }`}
                      data-testid={`day-tab-${i}`}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
                        isActive ? "text-white/80" : isToday ? "text-terracotta" : "text-ink-3"
                      }`}>{DAY_SHORT[i]}</span>
                      <span className="font-heading font-semibold text-base leading-tight mt-0.5">
                        {parseLocalDate(dayDates[i]).getDate()}
                      </span>
                      {count > 0 && (
                        <span className={`mt-1 font-data text-[10px] ${isActive ? "text-white/90" : "text-ink-3"}`}>
                          {count} mvt{count > 1 ? "s" : ""}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4"
              onTouchStart={(e) => {
                touchStartX.current = e.changedTouches[0].clientX;
              }}
              onTouchEnd={(e) => {
                if (touchStartX.current == null) return;
                const dx = e.changedTouches[0].clientX - touchStartX.current;
                if (Math.abs(dx) > 60) {
                  const next = dx < 0 ? activeDay + 1 : activeDay - 1;
                  safeSetActiveDay(Math.max(0, Math.min(6, next)));
                }
                touchStartX.current = null;
              }}
              data-testid="mobile-day-swipe-area"
            >
              {renderDayCard(program.days[activeDay], activeDay, "mobile")}
            </div>
          </div>

          {/* Desktop: full week grid */}
          <div className="hidden md:grid mt-8 grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
            {program.days.map((d, i) => renderDayCard(d, i, "desktop"))}
          </div>
        </>
      )}

      {!loading && program && user.role === "athlete" && ((program.days[activeDay].blocks || []).some((b) => b.type === "strength" && (b.movements || []).length > 0)) && (
        <Sheet>
          <SheetTrigger asChild>
            <button
              className="md:hidden fixed bottom-20 right-4 z-40 h-14 pl-4 pr-5 rounded-full bg-terracotta text-white shadow-lift flex items-center gap-2 active:scale-95 transition-transform"
              data-testid="fab-quick-log"
              aria-label="Quick log a set"
            >
              <Lightning size={18} weight="fill" />
              <span className="font-medium">Log set</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto border-line">
            <SheetHeader className="text-left">
              <SheetTitle className="font-heading text-2xl">Quick log</SheetTitle>
              <SheetDescription>
                {DAY_NAMES[activeDay]} · Tap a strength movement to record a set.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-5 space-y-2 pb-4">
              {(program.days[activeDay].blocks || [])
                .filter((b) => b.type === "strength")
                .flatMap((b) => (b.movements || []).map((mv) => ({ mv, blockId: b.id, blockType: b.type })))
                .map(({ mv, blockId, blockType }) => (
                  <QuickLogRow key={mv.id} mv={mv} program={program} dayIdx={activeDay} blockId={blockId} blockType={blockType} athleteId={athleteId} />
                ))}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {user.role !== "athlete" && athlete && (
        <div className="mt-8 hidden" />
      )}

      {canEditCore && (
        <>
          <BlockTypePicker
            open={blockPickerDay !== null}
            onOpenChange={(o) => !o && setBlockPickerDay(null)}
            onPick={pickBlockType}
            onPickWod={insertWodAsBlock}
            onPickTemplate={insertTemplateAsBlock}
          />
          {pendingBlockType && (
            <Dialog open={true} onOpenChange={(o) => !o && setPendingBlockType(null)}>
              <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">New {BLOCK_TYPES[pendingBlockType.type]?.label}</DialogTitle>
                  <DialogDescription>{BLOCK_TYPES[pendingBlockType.type]?.desc}</DialogDescription>
                </DialogHeader>
                <BlockForm
                  type={pendingBlockType.type}
                  onSave={async (blockData, movements) => {
                    await addBlock(pendingBlockType.dayIdx, pendingBlockType.type, blockData, movements);
                    setPendingBlockType(null);
                  }}
                  onCancel={() => setPendingBlockType(null)}
                />
              </DialogContent>
            </Dialog>
          )}
        </>
      )}

      {/* Confirmation guard when switching days — asked for so a stray tap
          on a day tab or an accidental swipe can't yank the view away from
          the day currently being worked on. Cancelled by tapping outside
          or by pressing "Stay on <current day>". */}
      <AlertDialog open={pendingDay !== null} onOpenChange={(o) => { if (!o) setPendingDay(null); }}>
        <AlertDialogContent data-testid="switch-day-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Switch day?</AlertDialogTitle>
            <AlertDialogDescription>
              You're currently on <b>{DAY_NAMES[activeDay]}</b>. Do you want to move to <b>{pendingDay !== null ? DAY_NAMES[pendingDay] : ""}</b>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="switch-day-cancel">
              Stay on {DAY_NAMES[activeDay]}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDaySwitch} data-testid="switch-day-confirm-btn">
              Yes, switch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function GoToDatePopover({ currentDate, onPick }) {
  const [open, setOpen] = useState(false);
  const selected = parseLocalDate(currentDate || todayISO());

  const pick = (dateObj) => {
    if (!dateObj) return;
    setOpen(false);
    onPick(toLocalISODate(dateObj));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 h-9 px-3 rounded-xl border border-line bg-white hover:bg-sage/40 text-ink text-sm font-medium transition-colors"
          data-testid="goto-date-pill"
          title="Jump to a specific date"
        >
          <CalendarBlank size={14} weight="bold" />
          <span className="hidden sm:inline">Go to date</span>
          <span className="sm:hidden">Date</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-2" data-testid="goto-date-popover">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={pick}
          initialFocus
        />
        <div className="flex items-center justify-between px-2 pb-2 pt-1 gap-2">
          <input
            type="date"
            defaultValue={toLocalISODate(selected)}
            onChange={(e) => {
              if (!e.target.value) return;
              pick(parseLocalDate(e.target.value));
            }}
            className="h-8 rounded-md border border-line px-2 text-sm text-ink bg-white flex-1"
            data-testid="goto-date-input"
          />
          <Button
            variant="ghost"
            className="rounded-lg h-8 px-3 text-xs"
            onClick={() => pick(parseLocalDate(todayISO()))}
            data-testid="goto-date-today"
          >
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

